import type { ApiRequester } from "@/shared/api";

type QueryValue = string | number | undefined;

export class OperatingRoomsApi {
  constructor(private readonly api: ApiRequester) {}

  listRooms(payload: iSharedOperatingRooms.ListOperatingRoomsPayloadDto = {}) {
    return this.api.get<iSharedOperatingRooms.ListOperatingRoomsResponseDto>({
      path: this.path("/operating-rooms", payload),
    });
  }
  getRoom(uid: string) {
    return this.api.get<iSharedOperatingRooms.OperatingRoomDto>({
      path: this.path("/operating-rooms/card", { uid }),
    });
  }
  createRoom(payload: iSharedOperatingRooms.CreateOperatingRoomPayloadDto) {
    return this.api.post<
      iSharedOperatingRooms.OperatingRoomDto,
      iSharedOperatingRooms.CreateOperatingRoomPayloadDto
    >({ path: "/operating-rooms", payload });
  }
  updateRoom(payload: iSharedOperatingRooms.UpdateOperatingRoomPayloadDto) {
    return this.api.patch<
      iSharedOperatingRooms.OperatingRoomDto,
      iSharedOperatingRooms.UpdateOperatingRoomPayloadDto
    >({ path: "/operating-rooms", payload });
  }
  archiveRoom(payload: iSharedOperatingRooms.ArchiveOperatingRoomPayloadDto) {
    return this.api.delete<
      iSharedOperatingRooms.ArchiveOperatingRoomResponseDto,
      iSharedOperatingRooms.ArchiveOperatingRoomPayloadDto
    >({ path: "/operating-rooms", payload });
  }
  changeRoomDeletionMark(
    payload: iSharedOperatingRooms.ChangeOperatingRoomDeletionMarkPayloadDto
  ) {
    return this.api.post<
      iSharedOperatingRooms.OperatingRoomDto,
      iSharedOperatingRooms.ChangeOperatingRoomDeletionMarkPayloadDto
    >({ path: "/operating-rooms/deletion-mark", payload });
  }
  changeRoomStatus(
    payload: iSharedOperatingRooms.ChangeOperatingRoomStatusPayloadDto
  ) {
    return this.api.post<
      iSharedOperatingRooms.OperatingRoomDto,
      iSharedOperatingRooms.ChangeOperatingRoomStatusPayloadDto
    >({ path: "/operating-rooms/status", payload });
  }

  listParticipantRoles() {
    return this.api.get<iSharedOperatingRooms.ListParticipantRolesResponseDto>({
      path: "/operating-rooms/participant-roles",
    });
  }
  listOperationTypes() {
    return this.api.get<iSharedOperatingRooms.ListOperationTypesResponseDto>({
      path: "/operating-rooms/operation-types",
    });
  }
  createOperationType(
    payload: iSharedOperatingRooms.CreateOperationTypePayloadDto
  ) {
    return this.api.post<
      iSharedOperatingRooms.OperationTypeDto,
      iSharedOperatingRooms.CreateOperationTypePayloadDto
    >({ path: "/operating-rooms/operation-types", payload });
  }
  updateOperationType(
    payload: iSharedOperatingRooms.UpdateOperationTypePayloadDto
  ) {
    return this.api.patch<
      iSharedOperatingRooms.OperationTypeDto,
      iSharedOperatingRooms.UpdateOperationTypePayloadDto
    >({ path: "/operating-rooms/operation-types", payload });
  }
  deleteOperationType(
    payload: iSharedOperatingRooms.DeleteOperationTypePayloadDto
  ) {
    return this.api.delete<
      iSharedOperatingRooms.DeleteOperationTypeResponseDto,
      iSharedOperatingRooms.DeleteOperationTypePayloadDto
    >({ path: "/operating-rooms/operation-types", payload });
  }
  listDiagnoses() {
    return this.api.get<iSharedOperatingRooms.ListDiagnosesResponseDto>({
      path: "/operating-rooms/diagnoses",
    });
  }
  createDiagnosis(payload: iSharedOperatingRooms.CreateDiagnosisPayloadDto) {
    return this.api.post<
      iSharedOperatingRooms.DiagnosisDto,
      iSharedOperatingRooms.CreateDiagnosisPayloadDto
    >({ path: "/operating-rooms/diagnoses", payload });
  }
  updateDiagnosis(payload: iSharedOperatingRooms.UpdateDiagnosisPayloadDto) {
    return this.api.patch<
      iSharedOperatingRooms.DiagnosisDto,
      iSharedOperatingRooms.UpdateDiagnosisPayloadDto
    >({ path: "/operating-rooms/diagnoses", payload });
  }
  deleteDiagnosis(payload: iSharedOperatingRooms.DeleteDiagnosisPayloadDto) {
    return this.api.delete<
      iSharedOperatingRooms.DeleteDiagnosisResponseDto,
      iSharedOperatingRooms.DeleteDiagnosisPayloadDto
    >({ path: "/operating-rooms/diagnoses", payload });
  }
  listHistory(payload: iSharedOperatingRooms.ListAuditEventsPayloadDto = {}) {
    return this.api.get<iSharedOperatingRooms.ListAuditEventsResponseDto>({
      path: this.path("/operating-rooms/history", payload),
    });
  }
  listOperations(payload: iSharedOperatingRooms.ListOperationsPayloadDto) {
    return this.api.get<iSharedOperatingRooms.ListOperationsResponseDto>({
      path: this.path("/operating-rooms/operations", payload),
    });
  }
  listCalendarOperationLoads(
    payload: iSharedOperatingRooms.CalendarOperationLoadsPayloadDto
  ) {
    return this.api.get<iSharedOperatingRooms.CalendarOperationLoadsResponseDto>({
      path: this.path("/operating-rooms/operations/calendar-loads", payload),
    });
  }
  getOperation(uid: string) {
    return this.api.get<iSharedOperatingRooms.OperationDto>({
      path: this.path("/operating-rooms/operations/card", { uid }),
    });
  }
  ensureOperationChat(
    payload: iSharedOperatingRooms.EnsureOperationChatPayloadDto
  ) {
    return this.api.post<
      iSharedOperatingRooms.EnsureOperationChatResponseDto,
      iSharedOperatingRooms.EnsureOperationChatPayloadDto
    >({ path: "/operating-rooms/operations/chat", payload });
  }
  checkAvailability(
    payload: iSharedOperatingRooms.CheckAvailabilityPayloadDto
  ) {
    return this.api.post<
      iSharedOperatingRooms.AvailabilityCheckResponseDto,
      iSharedOperatingRooms.CheckAvailabilityPayloadDto
    >({
      path: "/operating-rooms/operations/availability",
      payload,
      reportError: false,
    });
  }
  createOperation(payload: iSharedOperatingRooms.CreateOperationPayloadDto) {
    return this.api.post<
      iSharedOperatingRooms.OperationDto,
      iSharedOperatingRooms.CreateOperationPayloadDto
    >({ path: "/operating-rooms/operations", payload });
  }
  updateOperation(payload: iSharedOperatingRooms.UpdateOperationPayloadDto) {
    return this.api.patch<
      iSharedOperatingRooms.OperationDto,
      iSharedOperatingRooms.UpdateOperationPayloadDto
    >({ path: "/operating-rooms/operations", payload });
  }
  assignAnesthesiologist(
    payload: iSharedOperatingRooms.AssignAnesthesiologistPayloadDto
  ) {
    return this.api.patch<
      iSharedOperatingRooms.OperationDto,
      iSharedOperatingRooms.AssignAnesthesiologistPayloadDto
    >({ path: "/operating-rooms/operations/anesthesiologist", payload });
  }
  changeOperationStatus(
    payload: iSharedOperatingRooms.ChangeOperationStatusPayloadDto
  ) {
    return this.api.post<
      iSharedOperatingRooms.OperationDto,
      iSharedOperatingRooms.ChangeOperationStatusPayloadDto
    >({ path: "/operating-rooms/operations/status", payload });
  }

  private path<TPayload extends object>(
    base: `/${string}`,
    payload: TPayload
  ): `/${string}` {
    const query = new URLSearchParams();
    Object.entries(payload as Record<string, QueryValue>).forEach(
      ([key, value]) => {
        if (value !== undefined && value !== "") query.set(key, String(value));
      }
    );
    return `${base}${query.size ? `?${query.toString()}` : ""}` as `/${string}`;
  }
}
